import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { BookOpen } from 'lucide-react';

const gold = '#C9A227';
const goldLight = '#e6c45e';
const ivory = '#F5F0E8';
const navy = '#0d1b2a';
const navyMid = '#162a40';
const font = 'Frank Ruhl Libre, David, serif';

const Divider = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 1.5, gap: 1 }}>
    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent, ${gold}, transparent)`, opacity: 0.4 }} />
    <Typography sx={{ fontFamily: font, color: gold, fontSize: '0.6rem', opacity: 0.7 }}>✦</Typography>
    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent, ${gold}, transparent)`, opacity: 0.4 }} />
  </Box>
);

const CategoryHeader = ({ label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, mt: 1.25 }}>
    <Typography sx={{ fontFamily: font, fontSize: '0.58rem', color: gold, opacity: 0.6, letterSpacing: '0.1em' }}>✧</Typography>
    <Typography sx={{ fontFamily: font, fontSize: '0.62rem', color: gold, opacity: 0.6, letterSpacing: '0.1em', fontWeight: 600 }}>
      {label}
    </Typography>
  </Box>
);

const LimudRow = ({ labelHe, valueHe, url }) => (
  <Box sx={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    py: 0.6, borderBottom: `1px solid rgba(201,162,39,0.1)`,
    '&:last-child': { borderBottom: 'none' },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography sx={{ fontFamily: font, fontSize: '0.7rem', color: goldLight, fontWeight: 700 }}>
        {labelHe}
      </Typography>
      {url && (
        <BookOpen size={10} style={{ color: gold, opacity: 0.4, flexShrink: 0, marginTop: 2 }} />
      )}
    </Box>
    <Box sx={{ flex: 1, mx: 1, borderBottom: `1px dotted rgba(201,162,39,0.25)`, minWidth: 20, alignSelf: 'center', mb: '3px' }} />
    <Typography sx={{ fontFamily: font, fontSize: '0.68rem', color: ivory, fontWeight: 500, textShadow: '0 0 8px rgba(201,162,39,0.1)' }}>
      {valueHe}
    </Typography>
  </Box>
);

/**
 * Sefer — "כריכת ספר קודש"
 * Navy עמוק + זהב, מסגרת כפולה, שורות עם leader dots.
 */
const Sefer = ({ today, items, groups, loading, error, title, showTitle }) => {
  const hebrewDate = today?.hebrewDate?.rendered || '';
  const displayGroups = groups || [];

  // Flat fallback when groups aren't available (preview mode)
  const flatItems = !groups && Array.isArray(items) ? items : null;

  return (
    <Box sx={{
      width: '100%', height: '100%', overflow: 'hidden',
      background: `linear-gradient(180deg, ${navy} 0%, ${navyMid} 100%)`,
      fontFamily: font, direction: 'rtl', position: 'relative',
      p: 2.5,
      // Outer gold border
      '&::before': {
        content: '""', position: 'absolute', inset: '4px',
        border: `2.5px solid ${gold}`, borderRadius: '3px',
        opacity: 0.5, pointerEvents: 'none',
      },
      // Inner gold border
      '&::after': {
        content: '""', position: 'absolute', inset: '9px',
        border: `1px solid ${gold}`, borderRadius: '2px',
        opacity: 0.25, pointerEvents: 'none',
      },
    }}>
      <Box sx={{ position: 'relative', zIndex: 1, height: '100%', overflow: 'auto',
        '&::-webkit-scrollbar': { width: '2px' },
        '&::-webkit-scrollbar-thumb': { background: `rgba(201,162,39,0.35)`, borderRadius: '1px' },
      }}>
        {/* Header */}
        {showTitle && (
          <Box sx={{ textAlign: 'center', mb: 0.5 }}>
            <Typography sx={{
              fontFamily: font, fontSize: '0.85rem', fontWeight: 700, color: gold,
              letterSpacing: '0.15em', textShadow: `0 0 20px rgba(201,162,39,0.3)`,
            }}>
              {title}
            </Typography>
          </Box>
        )}

        {hebrewDate && (
          <Typography sx={{ textAlign: 'center', fontFamily: font, fontSize: '0.62rem', color: ivory, opacity: 0.7, mb: 0.5 }}>
            {hebrewDate}
          </Typography>
        )}

        <Divider />

        {/* Content */}
        <Box sx={{ px: 1 }}>
          {displayGroups.map((group) => (
            <Box key={group.category}>
              {displayGroups.length > 1 && <CategoryHeader label={group.label} />}
              {group.items.map((item) => (
                <LimudRow key={item.key} labelHe={item.labelHe} valueHe={item.valueHe} url={item.url} />
              ))}
            </Box>
          ))}

          {flatItems && flatItems.map((item, i) => (
            <LimudRow key={item.key || i} labelHe={item.labelHe} valueHe={item.valueHe} url={item.url} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Sefer;
